import { useEffect, useRef, useState } from 'react';
import {
  useForm,
  useWatch,
  type FieldErrors,
  type Resolver,
  type SubmitHandler,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { AlertCircle, ChevronDown, Loader2 } from 'lucide-react';
import {
  STORE_CONTACT_INTERNATIONAL_COUNTRY,
  STORE_CONTACT_INTERNATIONAL_INPUT_MAX_LENGTH,
  STORE_CONTACT_LOCAL_INPUT_MAX_LENGTH,
  STORE_CONTACT_SUPPORTED_COUNTRIES,
  contactMethodsSchema,
  getStoreContactCallingCode,
  getStoreContactCountryOption,
  mapStoreContactError,
  rehydrateStoreContactPhone,
  useTranslation,
  useUpdateStoreContactMethods,
} from '@dukanti/shared';
import type {
  ContactMethodsFormInput,
  ContactMethodsFormValues,
  StoreContactCountrySelectValue,
  StoreContactErrorKey,
  TranslationKey,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavBlocker } from '@/hooks/use-nav-blocker';

export type ContactMethodsFormDefaults = ContactMethodsFormInput;

export interface ContactMethodsFormProps {
  readonly storeId: string;
  readonly defaultValues: ContactMethodsFormDefaults;
  /** When true, the WhatsApp local-number input auto-focuses on mount. */
  readonly autoFocusWhatsapp?: boolean;
}

type ContactFieldName = 'whatsapp' | 'phone';

type ContactFieldErrorKey =
  | StoreContactErrorKey
  | 'admin.storeSettings.errors.atLeastOneRequired'
  | 'admin.storeSettings.errors.whatsappInvalidForCountry'
  | 'admin.storeSettings.errors.phoneInvalidForCountry'
  | 'admin.storeSettings.errors.whatsappInternationalInvalid'
  | 'admin.storeSettings.errors.phoneInternationalInvalid'
  | 'admin.storeSettings.errors.whatsappLocalTooLong'
  | 'admin.storeSettings.errors.phoneLocalTooLong'
  | 'admin.storeSettings.errors.whatsappInternationalTooLong'
  | 'admin.storeSettings.errors.phoneInternationalTooLong';

const contactMethodsResolver = zodResolver(contactMethodsSchema) as Resolver<
  ContactMethodsFormInput,
  unknown,
  ContactMethodsFormValues
>;

function toFormDefaults(values: ContactMethodsFormValues): ContactMethodsFormDefaults {
  const whatsapp = rehydrateStoreContactPhone(values.whatsappNumber);
  const phone = rehydrateStoreContactPhone(values.phoneNumber);
  return {
    whatsapp: {
      country: whatsapp.country,
      localNumber: whatsapp.localNumber,
    },
    phone: {
      country: phone.country,
      localNumber: phone.localNumber,
    },
  };
}

function getFieldError(
  errors: FieldErrors<ContactMethodsFormInput>,
  fieldName: ContactFieldName,
): ContactFieldErrorKey | undefined {
  return errors[fieldName]?.localNumber?.message as ContactFieldErrorKey | undefined;
}

interface StoreContactPhoneFieldProps {
  readonly fieldName: ContactFieldName;
  readonly labelKey: TranslationKey;
  readonly inputId: string;
  readonly countryTestId: string;
  readonly callingCodeTestId: string;
  readonly inputTestId: string;
  readonly errorTestId: string;
  readonly hintTestId: string;
  readonly selectedCountry: StoreContactCountrySelectValue;
  readonly errorKey: ContactFieldErrorKey | undefined;
  readonly register: ReturnType<typeof useForm<ContactMethodsFormInput>>['register'];
  readonly inputRef?: (element: HTMLInputElement | null) => void;
}

function StoreContactPhoneField({
  fieldName,
  labelKey,
  inputId,
  countryTestId,
  callingCodeTestId,
  inputTestId,
  errorTestId,
  hintTestId,
  selectedCountry,
  errorKey,
  register,
  inputRef,
}: StoreContactPhoneFieldProps) {
  const { t, dir } = useTranslation();
  const countryOption = getStoreContactCountryOption(selectedCountry);
  const isInternational = selectedCountry === STORE_CONTACT_INTERNATIONAL_COUNTRY;
  const badgeText = isInternational
    ? t(countryOption.translationKey)
    : getStoreContactCallingCode(selectedCountry);
  const numberRegister = register(`${fieldName}.localNumber`);

  return (
    <div>
      <Label htmlFor={inputId} className="text-foreground">
        {t(labelKey)}
      </Label>
      <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(10rem,1fr)_auto_minmax(11rem,1.5fr)] sm:items-end">
        <div className="min-w-0">
          <Label
            htmlFor={`${inputId}-country`}
            className="text-muted-foreground text-start text-xs font-medium"
          >
            {t('admin.storeSettings.countryLabel')}
          </Label>
          <div className="relative mt-1">
            <select
              id={`${inputId}-country`}
              className="border-input bg-background text-foreground ring-offset-background focus-visible:ring-ring h-10 w-full appearance-none rounded-md border bg-none px-3 py-2 pe-10 text-start text-base [-moz-appearance:none] [-webkit-appearance:none] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 md:text-sm"
              data-testid={countryTestId}
              aria-label={t('admin.storeSettings.countryLabel')}
              {...register(`${fieldName}.country`)}
            >
              {STORE_CONTACT_SUPPORTED_COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.flag} {t(country.translationKey)}
                </option>
              ))}
            </select>
            <ChevronDown
              className="text-muted-foreground pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2"
              aria-hidden
            />
          </div>
        </div>

        <div className="min-w-0">
          <span className="text-muted-foreground block text-start text-xs font-medium">
            {t('admin.storeSettings.callingCodeLabel')}
          </span>
          <span
            dir={isInternational ? dir : 'ltr'}
            className="border-input bg-muted text-foreground mt-1 flex min-h-10 items-center rounded-md border px-3 text-start text-sm font-medium"
            data-testid={callingCodeTestId}
          >
            {badgeText}
          </span>
        </div>

        <div className="min-w-0">
          <Label htmlFor={inputId} className="text-muted-foreground text-start text-xs font-medium">
            {t('admin.storeSettings.localPhoneLabel')}
          </Label>
          <Input
            id={inputId}
            type="tel"
            dir="ltr"
            autoComplete="tel"
            inputMode="tel"
            maxLength={
              isInternational
                ? STORE_CONTACT_INTERNATIONAL_INPUT_MAX_LENGTH
                : STORE_CONTACT_LOCAL_INPUT_MAX_LENGTH
            }
            placeholder={t(
              isInternational
                ? 'admin.storeSettings.internationalPhonePlaceholder'
                : 'admin.storeSettings.localPhonePlaceholderIL',
            )}
            aria-invalid={!!errorKey}
            aria-describedby={errorKey ? `${inputId}-error` : `${inputId}-hint`}
            data-testid={inputTestId}
            {...numberRegister}
            ref={(element) => {
              numberRegister.ref(element);
              inputRef?.(element);
            }}
          />
        </div>
      </div>
      {errorKey ? (
        <p
          id={`${inputId}-error`}
          role="alert"
          className="text-destructive mt-1 text-start text-sm"
          data-testid={errorTestId}
          data-error-key={errorKey}
        >
          {t(errorKey as TranslationKey)}
        </p>
      ) : null}
      <p
        id={`${inputId}-hint`}
        className="text-muted-foreground mt-1 text-start text-sm"
        data-testid={hintTestId}
      >
        {t('admin.storeSettings.phoneHintCountryFirst')}
      </p>
    </div>
  );
}

export function ContactMethodsForm({
  storeId,
  defaultValues,
  autoFocusWhatsapp = false,
}: Readonly<ContactMethodsFormProps>) {
  const { t, dir } = useTranslation();
  const [submitError, setSubmitError] = useState<StoreContactErrorKey | null>(null);
  const updateMutation = useUpdateStoreContactMethods(supabase);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ContactMethodsFormInput, unknown, ContactMethodsFormValues>({
    resolver: contactMethodsResolver,
    mode: 'onBlur',
    defaultValues,
  });

  useNavBlocker(isDirty);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const whatsappRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocusWhatsapp) {
      whatsappRef.current?.focus();
    }
  }, [autoFocusWhatsapp]);

  const whatsappCountry = useWatch({ control, name: 'whatsapp.country' });
  const phoneCountry = useWatch({ control, name: 'phone.country' });

  const onSubmit: SubmitHandler<ContactMethodsFormValues> = async (values) => {
    setSubmitError(null);
    try {
      await updateMutation.mutateAsync({
        storeId,
        whatsappNumber: values.whatsappNumber,
        phoneNumber: values.phoneNumber,
      });
      toast.success(t('admin.storeSettings.toasts.saved'));
      reset(toFormDefaults(values));
    } catch (err) {
      setSubmitError(mapStoreContactError(err));
    }
  };

  const whatsappErrorKey = getFieldError(errors, 'whatsapp');
  const phoneErrorKey = getFieldError(errors, 'phone');

  return (
    <form
      noValidate
      dir={dir}
      onSubmit={handleSubmit(onSubmit)}
      className="mt-8 space-y-6"
      data-testid="store-settings-form"
    >
      <div className="bg-card border-border rounded-xl border p-6 shadow-sm sm:p-8">
        <h2 className="font-display text-foreground text-start text-xl font-bold">
          {t('admin.storeSettings.whatsappLabel')}
        </h2>
        <p className="text-muted-foreground mt-2 text-start text-sm">
          {t('admin.storeSettings.fixBanner')}
        </p>

        <div className="bg-border my-4 h-px" aria-hidden="true" />

        <StoreContactPhoneField
          fieldName="whatsapp"
          labelKey="admin.storeSettings.whatsappLocalLabel"
          inputId="store-settings-whatsapp"
          countryTestId="store-settings-whatsapp-country"
          callingCodeTestId="store-settings-whatsapp-calling-code"
          inputTestId="store-settings-whatsapp"
          errorTestId="store-settings-whatsapp-error"
          hintTestId="store-settings-whatsapp-hint"
          selectedCountry={whatsappCountry ?? defaultValues.whatsapp.country}
          errorKey={whatsappErrorKey}
          register={register}
          inputRef={(element) => {
            whatsappRef.current = element;
          }}
        />

        <div className="mt-5">
          <StoreContactPhoneField
            fieldName="phone"
            labelKey="admin.storeSettings.phoneLocalLabel"
            inputId="store-settings-phone"
            countryTestId="store-settings-phone-country"
            callingCodeTestId="store-settings-phone-calling-code"
            inputTestId="store-settings-phone"
            errorTestId="store-settings-phone-error"
            hintTestId="store-settings-phone-hint"
            selectedCountry={phoneCountry ?? defaultValues.phone.country}
            errorKey={phoneErrorKey}
            register={register}
          />
        </div>

        <Button
          type="submit"
          disabled={updateMutation.isPending || !isDirty}
          aria-disabled={updateMutation.isPending || !isDirty}
          className="mt-8 w-full sm:w-auto"
          data-testid="store-settings-submit"
        >
          {updateMutation.isPending ? (
            <>
              <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden />
              {t('admin.storeSettings.submitting')}
            </>
          ) : (
            t('admin.storeSettings.submit')
          )}
        </Button>
      </div>

      {submitError ? (
        <div
          role="alert"
          data-testid="store-settings-submit-error"
          data-error-key={submitError}
          className="border-destructive/30 bg-destructive/10 text-destructive flex items-start gap-2 rounded-lg border p-3 text-start text-sm"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <p>{t(submitError as TranslationKey)}</p>
        </div>
      ) : null}
    </form>
  );
}
